import axios, { AxiosError } from 'axios';
import { CONTENT_API_PATH } from 'constants/api';
import { ContentInterface } from 'content/type';
import { ApiService } from './apiService';
import { FetchResult } from '../../type/apiType';
import { I18nFieldData } from '../../types/i18n/i18nTypes';
import { I18nApiErrorHandler } from '../../services/i18n/i18nApiErrorHandler';

// i18n 지원을 위한 확장된 콘텐츠 인터페이스
export interface ContentFormData extends ContentInterface {
  // 레거시 필드와 i18n 필드를 모두 포함하는 폼 데이터
  formMode?: 'legacy' | 'i18n';
}

// API 요청 시 사용할 인터페이스 (레거시와 i18n 필드 모두 포함)
export interface ContentApiRequest {
  // 레거시 필드
  title?: string;
  title_en?: string;
  synopsis?: string;
  synopsis_en?: string;
  seo_title?: string;
  seo_synopsis?: string;
  seo_keyword?: string;

  // i18n 필드
  i18n_title?: I18nFieldData;
  i18n_synopsis?: I18nFieldData;
  i18n_seo_title?: I18nFieldData;
  i18n_seo_synopsis?: I18nFieldData;
  i18n_seo_keyword?: I18nFieldData;

  // 기타 필드들
  [key: string]: any;
}

class ContentApiService extends ApiService<ContentInterface> {
  /**
   * i18n 필드를 포함한 콘텐츠 목록 조회
   */
  public async fetchContentsWithI18n(
    queryParams?: URLSearchParams
  ): Promise<FetchResult<ContentInterface>> {
    try {
      const params = new URLSearchParams(queryParams);

      // i18n 필드를 포함한 필드 목록 설정
      const i18nFields = [
        'id',
        'title',
        'title_en',
        'synopsis',
        'synopsis_en',
        'seo_title',
        'seo_synopsis',
        'seo_keyword',
        'i18n_title',
        'i18n_synopsis',
        'i18n_seo_title',
        'i18n_seo_synopsis',
        'i18n_seo_keyword',
        'slug',
        'status',
        'created',
        'modified',
      ].join(',');
      params.set('fields', i18nFields);

      const response = await axios.get(this.constructUrl(), { params });
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * i18n 필드를 포함한 단일 콘텐츠 조회
   */
  public async fetchContentWithI18n(id: number): Promise<ContentInterface> {
    try {
      const fields = [
        'id',
        'title',
        'title_en',
        'synopsis',
        'synopsis_en',
        'seo_title',
        'seo_synopsis',
        'seo_keyword',
        'i18n_title',
        'i18n_synopsis',
        'i18n_seo_title',
        'i18n_seo_synopsis',
        'i18n_seo_keyword',
        'slug',
        'status',
        'created',
        'modified',
      ].join(',');

      const params = { fields };
      const response = await axios.get(this.constructUrl(id), { params });
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * 레거시 필드와 i18n 필드를 모두 포함한 콘텐츠 생성
   */
  public async createContentWithI18n(contentData: ContentApiRequest): Promise<ContentInterface> {
    try {
      const response = await axios.post(this.constructUrl(), contentData);
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * 레거시 필드와 i18n 필드를 모두 포함한 콘텐츠 업데이트
   */
  public async updateContentWithI18n(
    id: number,
    contentData: Partial<ContentApiRequest>
  ): Promise<ContentInterface> {
    try {
      const response = await axios.patch(this.constructUrl(id), contentData);
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * 특정 언어의 콘텐츠 목록 조회 (언어별 필터링)
   */
  public async fetchContentsByLanguage(
    language: string,
    queryParams?: URLSearchParams
  ): Promise<FetchResult<ContentInterface>> {
    try {
      const params = new URLSearchParams(queryParams);

      // 특정 언어의 번역이 있는 콘텐츠만 필터링
      params.set('has_translation', language);

      return this.fetchContentsWithI18n(params);
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * 번역 완성도별 콘텐츠 조회
   */
  public async fetchContentsByTranslationStatus(
    requiredLanguages: string[] = ['ko'],
    queryParams?: URLSearchParams
  ): Promise<FetchResult<ContentInterface>> {
    try {
      const params = new URLSearchParams(queryParams);

      // 필수 언어 번역 상태 필터
      params.set('required_languages', requiredLanguages.join(','));

      return this.fetchContentsWithI18n(params);
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }
}

export const contentApiService = new ContentApiService(CONTENT_API_PATH);
